// AGC: MaintenanceForm - Form for adding/editing machine maintenance records
// AGC: Includes type selection, date, hours, cost, provider, description

import React, { useState } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import type { MachineInstance } from '../../types/machineInstance'
import type {
  MachineMaintenanceRecord,
  MachineMaintenanceType,
  CreateMaintenanceRecordRequest,
} from '../../types/machineMaintenance'
import { MAINTENANCE_TYPES } from '../../types/machineMaintenance'
import { X, Save, Loader2, Wrench, Calendar, Clock, DollarSign, Building2, FileText, CheckSquare, Check } from 'lucide-react'

interface MaintenanceFormProps {
  machine: MachineInstance
  existingRecord?: MachineMaintenanceRecord
  onSave: (data: CreateMaintenanceRecordRequest) => Promise<void>
  onCancel: () => void
  isSubmitting?: boolean
}

/**
 * AGC: MaintenanceForm component
 */
export const MaintenanceForm: React.FC<MaintenanceFormProps> = ({
  machine,
  existingRecord,
  onSave,
  onCancel,
  isSubmitting = false,
}) => {
  const { t } = useTranslation()
  const isEditing = !!existingRecord

  // AGC: Form state - Support multi-selection of maintenance types
  const [selectedTypes, setSelectedTypes] = useState<MachineMaintenanceType[]>(
    existingRecord?.types || (existingRecord?.type ? [existingRecord.type] : [])
  )
  const [date, setDate] = useState(
    existingRecord?.date || new Date().toISOString().split('T')[0]
  )
  const [hoursAtMaintenance, setHoursAtMaintenance] = useState(
    existingRecord?.hoursAtMaintenance?.toString() || machine.horimeterHours.toString()
  )
  const [cost, setCost] = useState(existingRecord?.cost?.toString() || '')
  const [provider, setProvider] = useState(existingRecord?.provider || '')
  const [description, setDescription] = useState(existingRecord?.description || '')
  const [notes, setNotes] = useState(existingRecord?.notes || '')
  const [updateSchedule, setUpdateSchedule] = useState(true)

  // AGC: Validation
  const [errors, setErrors] = useState<Record<string, string>>({})

  // AGC: Toggle maintenance type selection
  const toggleType = (typeId: MachineMaintenanceType) => {
    setSelectedTypes(prev => 
      prev.includes(typeId)
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    )
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    // AGC: At least one maintenance type must be selected
    if (selectedTypes.length === 0) {
      newErrors.types = 'Selecione pelo menos um tipo de manutenção'
    }

    if (!description.trim()) {
      newErrors.description = 'A descrição é obrigatória'
    }

    const hours = parseFloat(hoursAtMaintenance)
    if (isNaN(hours) || hours < 0) {
      newErrors.hoursAtMaintenance = 'Horas inválidas'
    }

    if (cost && isNaN(parseFloat(cost))) {
      newErrors.cost = 'Custo inválido'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) return

    // AGC: Use first selected type as primary, include all types in array
    const data: CreateMaintenanceRecordRequest = {
      machineId: machine.id,
      type: selectedTypes[0] || 'other',  // AGC: Primary type for backward compatibility
      types: selectedTypes,                // AGC: All selected types
      date,
      hoursAtMaintenance: parseFloat(hoursAtMaintenance),
      description: description.trim(),
      updatePreventiveSchedule: updateSchedule,
    }

    // AGC: Add optional fields
    if (cost) {
      data.cost = parseFloat(cost)
      data.currency = 'BRL' // AGC: Default to BRL, could be configurable
    }
    if (provider.trim()) {
      data.provider = provider.trim()
    }
    if (notes.trim()) {
      data.notes = notes.trim()
    }

    await onSave(data)
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
        {/* AGC: Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {isEditing
              ? t('machines.detail.maintenanceForm.editTitle')
              : t('machines.detail.maintenanceForm.title')}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* AGC: Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* AGC: Type Selection - Multi-select with checkboxes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Wrench className="w-4 h-4 inline mr-1" />
              {t('machines.detail.maintenanceForm.type')}
              <span className="text-xs text-gray-500 ml-2">
                ({t('machines.detail.maintenanceForm.selectMultiple')})
              </span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {MAINTENANCE_TYPES.map((mt) => {
                const isSelected = selectedTypes.includes(mt.id)
                return (
                  <button
                    key={mt.id}
                    type="button"
                    onClick={() => toggleType(mt.id)}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors text-left flex items-center gap-2 ${
                      isSelected
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                        : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    {/* AGC: Checkbox indicator */}
                    <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-600'
                    }`}>
                      {isSelected && <Check className="w-3 h-3" />}
                    </div>
                    <span className="mr-1">{mt.icon}</span>
                    <span className="truncate">{t(`machines.detail.maintenanceType.${mt.id}`)}</span>
                  </button>
                )
              })}
            </div>
            {errors.types && (
              <p className="mt-1 text-xs text-red-500">{errors.types}</p>
            )}
            {selectedTypes.length > 0 && (
              <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">
                {t('machines.detail.maintenanceForm.selectedCount', { count: selectedTypes.length })}
              </p>
            )}
          </div>

          {/* AGC: Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              {t('machines.detail.maintenanceForm.date')}
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          {/* AGC: Hours at Maintenance */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Clock className="w-4 h-4 inline mr-1" />
              {t('machines.detail.maintenanceForm.hoursAtMaintenance')}
            </label>
            <input
              type="number"
              step="0.1"
              value={hoursAtMaintenance}
              onChange={(e) => setHoursAtMaintenance(e.target.value)}
              className={`w-full px-3 py-2 bg-white dark:bg-gray-700 border rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                errors.hoursAtMaintenance
                  ? 'border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('machines.detail.maintenanceForm.hoursHelp', {
                hours: machine.horimeterHours.toFixed(1),
              })}
            </p>
            {errors.hoursAtMaintenance && (
              <p className="mt-1 text-xs text-red-500">{errors.hoursAtMaintenance}</p>
            )}
          </div>

          {/* AGC: Cost (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <DollarSign className="w-4 h-4 inline mr-1" />
              {t('machines.detail.maintenanceForm.costOptional')}
            </label>
            <input
              type="number"
              step="0.01"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="0.00"
              className={`w-full px-3 py-2 bg-white dark:bg-gray-700 border rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                errors.cost ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {errors.cost && <p className="mt-1 text-xs text-red-500">{errors.cost}</p>}
          </div>

          {/* AGC: Provider (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Building2 className="w-4 h-4 inline mr-1" />
              {t('machines.detail.maintenanceForm.providerOptional')}
            </label>
            <input
              type="text"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              placeholder="Nome do prestador de serviço..."
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          {/* AGC: Description (Required) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <FileText className="w-4 h-4 inline mr-1" />
              {t('machines.detail.maintenanceForm.description')} *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('machines.detail.maintenanceForm.descriptionPlaceholder')}
              rows={3}
              className={`w-full px-3 py-2 bg-white dark:bg-gray-700 border rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none ${
                errors.description
                  ? 'border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {errors.description && (
              <p className="mt-1 text-xs text-red-500">{errors.description}</p>
            )}
          </div>

          {/* AGC: Notes (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('machines.detail.maintenanceForm.notesOptional')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
            />
          </div>

          {/* AGC: Update Schedule Checkbox */}
          <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <input
              type="checkbox"
              id="updateSchedule"
              checked={updateSchedule}
              onChange={(e) => setUpdateSchedule(e.target.checked)}
              className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
            />
            <div>
              <label
                htmlFor="updateSchedule"
                className="text-sm font-medium text-gray-900 dark:text-white cursor-pointer"
              >
                {t('machines.detail.maintenanceForm.updateSchedule')}
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {t('machines.detail.maintenanceForm.updateScheduleHelp')}
              </p>
            </div>
          </div>

          {/* AGC: Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              {t('machines.detail.maintenanceForm.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('machines.detail.maintenanceForm.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {t('machines.detail.maintenanceForm.save')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default MaintenanceForm
